import { useIframeStore } from "app/iframeStore";
import { useCountriesStore } from "app/store";
import { useConstants } from "features/constantsProvider/context";
import { useI18n } from "features/translation/context";
import { useMemo } from "react";
import type { Location, LoaderFunctionArgs } from "react-router";
import type { UserCountrySelection } from "ui-kit";
import IFrameLoader from "ui-kit/src/IFrameLoader";

interface IframeProps {
	url: string;
}

export interface IframeSyncedLocationState {
	iframeKey: string;
}

export function getIframeKey(location: Location<IframeSyncedLocationState | undefined>): string {
	return location.state?.iframeKey ?? location.key;
}

function getSelectionKey(selection: UserCountrySelection): string {
	return Object.entries(selection.countries)
		.flatMap((continent) => {
			return continent[1];
		})
		.join("-");
}

export const iframeLoader = ({ request }: LoaderFunctionArgs) => {
	if (request.url !== window.location.toString()) {
		useIframeStore.getState().actions.startIframeLoading();
	}
	return null;
};

export default function Iframe({ url }: IframeProps) {
	const i18n = useI18n();
	const { loading } = useIframeStore();
	const { selection } = useCountriesStore();

	const { apiURL } = useConstants();

	const selectionKey = getSelectionKey(selection);
	// biome-ignore lint/correctness/useExhaustiveDependencies: url changes from replaceUrl must not reload the iframe
	const frameUrl = useMemo(() => {
		const iframeSrc = new URL(`${apiURL}${`/${url}`.replaceAll("//", "/")}`);
		iframeSrc.searchParams.set("_locale", i18n.locale);
		return iframeSrc;
	}, [apiURL, i18n.locale, selectionKey]);

	const key = `${frameUrl.toString()}${selectionKey}`;

	return (
		<>
			<iframe
				key={key}
				src={frameUrl.toString()}
				className={frameUrl.toString().includes("my-eyeball") ? "my-account" : ""}
				width="100%"
				style={{ height: "100vh", visibility: loading ? "hidden" : "visible" }}
				allowFullScreen
				allow="clipboard-read; clipboard-write"
			/>
			{loading ? <IFrameLoader /> : null}
		</>
	);
}
